import { useQuery } from 'react-query';
import { getStoreUseCases, getPersonnelStorage } from '@personnel/composition';
import type { Store } from '@personnel/domain/entities/Store';

/**
 * Hook for accessing stores data within Personnel domain
 *
 * Uses composition layer for dependency injection.
 * Automatically uses current organization UUID if not provided.
 * Uses React Query for caching and automatic updates.
 *
 * @param {Object} [request] - Store query parameters (optional)
 * @param {string} [request.organizationUuid] - Organization UUID (defaults to current organization)
 * @param {string} [request.storeUuid] - Specific store UUID to fetch
 * @returns {Object} Query result with stores array
 * @returns {Store[]} returns.data - Array of stores (empty array if loading/error)
 * @returns {boolean} returns.isLoading - Whether stores are being fetched
 * @returns {boolean} returns.isError - Whether an error occurred
 * @returns {Error} returns.error - Error object if request failed
 *
 * @example
 * // Use current organization
 * const { data: stores, isLoading } = usePersonnelStores();
 *
 * @example
 * // Use specific organization
 * const { data: stores } = usePersonnelStores({
 *   organizationUuid: 'specific-org-uuid'
 * });
 *
 * @example
 * // Fetch specific store
 * const { data: stores } = usePersonnelStores({
 *   storeUuid: 'store-uuid'
 * });
 */
export const usePersonnelStores = (request?: { organizationUuid?: string; storeUuid?: string }) => {
  const { getStores } = getStoreUseCases();

  // Default to current organization if not provided
  const queryRequest = {
    organizationUuid: request?.organizationUuid ?? getPersonnelStorage().getCurrentOrganizationUuid(),
    storeUuid: request?.storeUuid,
  };

  return useQuery<Store[]>({
    queryKey: ['personnel', 'stores', queryRequest],
    queryFn: () => getStores.execute(queryRequest),
  });
};
